// Intl-based date/time formatting for Dashboard V2. Kept dependency-free (no
// `@/util/date`, which transitively pulls in app chrome) and timezone-aware.

const DEFAULT_TZ = 'America/New_York';

const time = (iso: string, tz: string): string =>
  new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
    .format(new Date(iso))
    .replace(/\s/g, '')
    .toUpperCase();

const monthDay = (iso: string, tz: string): string =>
  new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    month: 'short',
    day: 'numeric',
  }).format(new Date(iso));

// "Jun 18, 6:00PM-12:00AM"
export const formatShiftDateTime = (
  startIso?: string | null,
  endIso?: string | null,
  tz?: string | null,
): string => {
  if (!startIso) return '';
  const zone = tz || DEFAULT_TZ;
  const date = monthDay(startIso, zone);
  const start = time(startIso, zone);
  const end = endIso ? time(endIso, zone) : '';
  return end ? `${date}, ${start}-${end}` : `${date}, ${start}`;
};

/**
 * "8:00 AM", or "--:--" when no check-in/out was captured.
 *
 * GRAVY-10819. This was `hourCycle: 'h23'`, so every worker row in Needs
 * Approval and Missing Timesheet read "10:20" with no meridiem — ops could not
 * tell a morning check-in from an evening one, which is exactly the kind of
 * error this screen exists to catch. The design shows 12-hour with AM/PM
 * throughout (Figma 39621:199138 and siblings), and the card's own date column
 * above these rows was already 12-hour via `formatShiftDateTime` — so a single
 * card disagreed with itself.
 *
 * Both call sites (`CheckEventCell`, `MissingTimesheetShiftCard`) want the same
 * thing, so the function changes rather than gaining a second variant nobody
 * would know to choose between.
 *
 * The space before AM/PM is normalised: current ICU emits U+202F (narrow
 * no-break space) there, which looks right and fails an ordinary `'8:00 AM'`
 * comparison in a test or a Playwright matcher.
 */
export const formatClock = (
  iso?: string | null,
  tz?: string | null,
): string => {
  if (!iso) return '--:--';
  return new Intl.DateTimeFormat('en-US', {
    timeZone: tz || DEFAULT_TZ,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
    .format(new Date(iso))
    .replace(/[\u202f\u00a0]/g, ' ');
};

// Break / Total durations: "H:MM" or "--" when unset.
export const formatDuration = (minutes?: number | null): string => {
  if (minutes == null) return '--';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
};

// "Jun 16, 5:30 AM"
export const formatMarkedAt = (
  iso?: string | null,
  tz?: string | null,
): string => {
  if (!iso) return '';
  const zone = tz || DEFAULT_TZ;
  const t = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(new Date(iso));
  return `${monthDay(iso, zone)}, ${t}`;
};
